"use client"

import { useState, useEffect } from "react"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ArrowLeft,
  User,
  Calendar,
  MessageSquare,
  Save,
  Target,
  Volume2,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ResponseCard } from "@/components/response-card"
import { parseResponseContent } from "@/lib/response-parser"
import { useLanguage } from "@/contexts/language-context"
import { useRouter } from "next/navigation"
import { useAuth } from "@/contexts/auth-context"
import { useCommunicationOptions } from "@/hooks/use-communication-options"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface ShowcaseItem {
  id: string
  user_input: string
  ai_response: string
  created_at: string
  user_email: string
  user_id: string
  description?: string | null
  scenario?: string | null
  target?: string | null
  tone?: string | null
}

interface UserInfo {
  email: string
  displayName: string
}

export default function ShowcasePage() {
  const { t } = useLanguage()
  const router = useRouter()
  const { isAuthenticated, userSubscription } = useAuth()
  const { scenarios: allScenarios, targets: allTargets, tones: allTones, getScenarioLabel, getTargetLabel, getToneLabel } = useCommunicationOptions()
  const [items, setItems] = useState<ShowcaseItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedItem, setSelectedItem] = useState<ShowcaseItem | null>(null)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [userItems, setUserItems] = useState<ShowcaseItem[]>([])
  const [isLoadingUserItems, setIsLoadingUserItems] = useState(false)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateSaved, setTemplateSaved] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const itemsPerPage = 10
  const isFree = !isAuthenticated || userSubscription?.plan === 'free'

  const [sortBy, setSortBy] = useState<string>('newest')
  const [filterScenario, setFilterScenario] = useState<string>('all')
  const [filterTarget, setFilterTarget] = useState<string>('all')
  const [filterTone, setFilterTone] = useState<string>('all')
  const { toast } = useToast()



  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  // 获取用户显示名称
  const getUserDisplayName = (email: string): string => {
    const username = email.split('@')[0]
    return username.charAt(0).toUpperCase() + username.slice(1)
  }

  // 获取所有公开记录
  const fetchShowcaseItems = async (page: number = 1, loadMore: boolean = false) => {
    try {
      if (loadMore) {
        setIsLoadingMore(true)
      } else {
        setLoading(true)
      }
      const params = new URLSearchParams({
        page: String(page),
        limit: String(itemsPerPage),
      })
      if (sortBy !== 'newest') params.append('sortBy', sortBy)
      if (filterScenario !== 'all') params.append('filterScenario', filterScenario)
      if (filterTarget !== 'all') params.append('filterTarget', filterTarget)
      if (filterTone !== 'all') params.append('filterTone', filterTone)

      const response = await fetch(`/api/showcase?${params.toString()}`)
      if (response.ok) {
        const data = await response.json()
        if (page === 1) {
          setItems(data.items || [])
        } else {
          setItems(prev => [...prev, ...(data.items || [])])
        }
        setHasMore(data.hasMore || false)
      } else {
        console.error('API response not ok:', response.status, response.statusText)
        const errorData = await response.text()
        console.error('Error response:', errorData)
      }
    } catch (error) {
      console.error('Error fetching showcase items:', error)
    } finally {
      if (loadMore) {
        setIsLoadingMore(false)
      } else {
        setLoading(false)
      }
    }
  }

  // 获取特定用户的公开记录
  const fetchUserItems = async (userId: string) => {
    try {
      setIsLoadingUserItems(true)
      const response = await fetch(`/api/showcase/user/${encodeURIComponent(userId)}`)
      if (response.ok) {
        const data = await response.json()
        setUserItems(data.items || [])
      } else {
        console.error('User API response not ok:', response.status, response.statusText)
        const errorData = await response.text()
        console.error('User API error response:', errorData)
      }
    } catch (error) {
      console.error('Error fetching user items:', error)
    } finally {
      setIsLoadingUserItems(false)
    }
  }

  // 保存为模板
  const saveAsTemplate = async (item: ShowcaseItem) => {
    if (!item) return
    setIsSavingTemplate(true)
    try {
      // 使用description作为模板内容，如果为null则使用"No description"
      const templateContent = item.description || "No description"
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: `Template ${new Date().toLocaleString()}`,
          scenario: item.scenario,
          target: item.target,
          tone: item.tone,
          content: templateContent
        })
      })
      if (!res.ok) throw new Error('Failed')
      toast({ title: 'Saved', description: 'Template saved successfully' })
      setTemplateSaved(true)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('templates-changed'))
      }
    } catch (err) {
      console.error(err)
      toast({ title: 'Error', description: 'Failed to save template', variant: 'destructive' })
    } finally {
      setIsSavingTemplate(false)
    }
  }

  useEffect(() => {
    fetchShowcaseItems()
    setSelectedItem(null)
  }, [])

  useEffect(() => {
    setIsSavingTemplate(false)
    setTemplateSaved(false)
  }, [selectedItem])

  useEffect(() => {
    fetchShowcaseItems(1)
    setCurrentPage(1)
    setSelectedItem(null)
  }, [sortBy, filterScenario, filterTarget, filterTone])

  const loadMore = () => {
    const nextPage = currentPage + 1
    setCurrentPage(nextPage)
    fetchShowcaseItems(nextPage, true)
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isLoadingMore && !selectedUser) {
      loadMore()
    }
  }

  const handleUserClick = (userId: string, email: string) => {
    setSelectedUser(email)
    setSelectedItem(null)
    setHasMore(false)
    setIsLoadingMore(false)
    setCurrentPage(1)
    fetchUserItems(userId)
  }

  const handleBackToShowcase = () => {
    setSelectedUser(null)
    setUserItems([])
    setSelectedItem(null)
    setIsLoadingUserItems(false)
    setHasMore(true)
    setCurrentPage(1)
    fetchShowcaseItems(1)
  }

  const displayItems = selectedUser ? userItems : items

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">{t('showcase.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.back()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('common.back')}
            </Button>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {t('showcase.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {selectedUser 
              ? `${t('showcase.userPrefix')} ${getUserDisplayName(selectedUser)}`
              : t('showcase.subtitle')
            }
          </p>
        </div>

        {/* User view back button */}
        {selectedUser && (
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={handleBackToShowcase}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('showcase.backAll')}
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 dark:bg-zinc-800 rounded-lg">
          <div className="flex items-center gap-2">
      <label className="text-sm font-medium">{t('showcase.sortBy')}</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
        <SelectItem value="newest">{t('showcase.sort.newest')}</SelectItem>
        <SelectItem value="oldest">{t('showcase.sort.oldest')}</SelectItem>
        <SelectItem value="scenario">{t('showcase.sort.scenario')}</SelectItem>
        <SelectItem value="target">{t('showcase.sort.target')}</SelectItem>
        <SelectItem value="tone">{t('showcase.sort.tone')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
      <label className="text-sm font-medium">{t('showcase.filter.scenario')}</label>
            <Select value={filterScenario} onValueChange={setFilterScenario}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
        <SelectItem value="all">{t('showcase.filter.all')}</SelectItem>
                {allScenarios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
      <label className="text-sm font-medium">{t('showcase.filter.target')}</label>
            <Select value={filterTarget} onValueChange={setFilterTarget}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
        <SelectItem value="all">{t('showcase.filter.all')}</SelectItem>
                {allTargets.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
      <label className="text-sm font-medium">{t('showcase.filter.tone')}</label>
            <Select value={filterTone} onValueChange={setFilterTone}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
        <SelectItem value="all">{t('showcase.filter.all')}</SelectItem>
                {allTones.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Items Grid */}
        <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
          {selectedUser && isLoadingUserItems ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">{t('showcase.loadingUser')}</p>
            </div>
          ) : displayItems.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {selectedUser ? t('showcase.none.userTitle') : t('showcase.none.globalTitle')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {selectedUser ? t('showcase.none.userDesc') : t('showcase.none.globalDesc')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayItems.map((item) => (
                  <Card
                    key={item.id}
                    className="hover-glow-card cursor-pointer bg-card-color transition-shadow"
                    onClick={() => setSelectedItem(item)}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(item.created_at)}
                        </div>
                        <div className="text-sm">
                          <p className="line-clamp-3">{truncateText(item.user_input)}</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {item.scenario && (
                            <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                              {getScenarioLabel(item.scenario || 'none')}
                            </span>
                          )}
                          {item.target && (
                            <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                              {getTargetLabel(item.target || 'none')}
                            </span>
                          )}
                          {item.tone && (
                            <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                              {getToneLabel(item.tone || 'none')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
        {isLoadingMore && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-500">{t('showcase.loadingMore')}</span>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>

    <Dialog open={!!selectedItem} onOpenChange={(v) => !v && setSelectedItem(null)}>
      {selectedItem && (
        <DialogContent className="max-w-3xl bg-primary max-h-[90vh] overflow-y-auto flex flex-col bg-gray-color">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItem(null)}
                className="p-1 bg-white text-black hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              {t('showcase.messageDetails')}
            </DialogTitle>
            <DialogDescription>
              {t('history.sharedBy')}{' '}
              <button
                onClick={() => {
                  setSelectedItem(null)
                  handleUserClick(selectedItem.user_id, selectedItem.user_email)
                }}
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                {getUserDisplayName(selectedItem.user_email)}
              </button>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mr-4">
              <Card>
                <CardContent className="p-4 bg-card-color">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <Calendar className="w-4 h-4" />
                    {t('showcase.field.date')}
                  </div>
                  <div className="font-medium">{formatDate(selectedItem.created_at)}</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 bg-card-color">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <MessageSquare className="w-4 h-4" />
                    {t('showcase.field.scenario')}
                  </div>
                  <div className="font-medium">{getScenarioLabel(selectedItem.scenario || 'none')}</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 bg-card-color">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <Target className="w-4 h-4" />
                    {t('showcase.field.target')}
                  </div>
                  <div className="font-medium">{getTargetLabel(selectedItem.target || 'none')}</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 bg-card-color">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <Volume2 className="w-4 h-4" />
                    {t('showcase.field.tone')}
                  </div>
                  <div className="font-medium">{getToneLabel(selectedItem.tone || 'none')}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-6 bg-zinc-color">
                <h3 className="text-lg font-semibold mb-3">{t('showcase.originalMessage')}</h3>
                <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg p-4 bg-card-color">
                  <p className="whitespace-pre-wrap">{selectedItem.user_input}</p>
                </div>
              </CardContent>
            </Card>

            {selectedItem.description && (
              <Card>
                <CardContent className="p-6 bg-zinc-color">
                  <h3 className="text-lg font-semibold mb-3">{t('showcase.description')}</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-500">
                    <p className="whitespace-pre-wrap text-blue-800 dark:text-blue-200">{selectedItem.description}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <h3 className="text-lg font-semibold mb-3">{t('showcase.aiResponse')}</h3>
              {(() => {
                const { response, translation } = parseResponseContent(selectedItem.ai_response)
                return <ResponseCard response={response} translation={translation} isStreaming={false} />
              })()}
            </div>

            <div className="pt-4 border-t flex flex-col items-end">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => saveAsTemplate(selectedItem)}
                      disabled={isSavingTemplate || templateSaved || isFree}
                      className="flex items-center gap-2 bg-botton-color"
                    >
                      {isSavingTemplate ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : templateSaved ? (
                        t('templates.saved')
                      ) : (
                        t('templates.saveAsTemplate')
                      )}
                    </Button>
                  </TooltipTrigger>
                  {/* {isFree && (
                    <TooltipContent>Only for subscribed users(Pro/Max)</TooltipContent>
                  )} */}
                </Tooltip>
              </TooltipProvider>
              {isFree && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("restriction.proMaxOnly")}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
    </>
  )
}
